import { useId, useState } from "react";

const Input = ({
  label,
  type = "text",
  placeholder,
  name,
  required = false,
  classes,
  isPassword = false,
  field,
  error,
  isMobile,
  isPercentage,
  onKeyDown,
  isCurrency,
  disabled = false,
}) => {
  const [showPassword, setShowPassword] = useState(false);
  const generatedId = useId();
  const inputId = name || generatedId;
  const errorId = `${inputId}-error`;

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const formatCurrency = (value) => {
    if (!value) return "";
    const numericValue = value.toString().replace(/[^0-9.]/g, "");
    const endsWithDot = numericValue.endsWith(".");
    const parts = numericValue.split(".");
    const integerPart = parts[0];
    let decimalPart = parts[1] || "";

    if (!integerPart) return "";

    const formattedInteger = parseInt(integerPart).toLocaleString("en-US");

    if (endsWithDot && decimalPart === "") {
      return `${formattedInteger}.`;
    }

    if (decimalPart) {
      return `${formattedInteger}.${decimalPart}`;
    }

    return formattedInteger;
  };

  const getRawValue = (formattedValue) => {
    return formattedValue.replace(/,/g, "");
  };

  const handleChange = (e) => {
    const inputValue = e.target.value;

    if (isCurrency) {
      const rawValue = getRawValue(inputValue);
      field.onChange(rawValue);
    } else if (isPercentage) {
      if (inputValue === "" || /^\d*\.?\d{0,2}$/.test(inputValue)) {
        field.onChange(inputValue);
      }
    } else {
      field.onChange(inputValue.trimStart());
    }
  };

  const getDisplayValue = () => {
    if (isCurrency && field?.value) {
      return formatCurrency(field.value);
    }
    return field?.value ?? "";
  };

  return (
    <div className="flex flex-col gap-y-2 flex-1">
      {label && (
        <label
          htmlFor={inputId}
          className="block text-base font-medium text-black"
        >
          {label}
          {required && (
            <span className="text-red-700 ml-1" aria-hidden="true">
              *
            </span>
          )}
          {required && <span className="sr-only"> (required)</span>}
        </label>
      )}
      <div className="relative flex items-center gap-x-2 px-6 py-3.5 border border-neutral-200 rounded-sm group focus-within:outline focus-within:outline-2 focus-within:outline-blue-600">
        {isMobile && (
          <>
            <span className="text-sm" aria-hidden="true">
              +1
            </span>
            <span className="text-neutral-500" aria-hidden="true">
              |
            </span>
          </>
        )}
        {type === "number" && isPercentage && (
          <>
            <span className="text-sm" aria-hidden="true">
              %
            </span>
            <span className="text-neutral-500" aria-hidden="true">
              |
            </span>
          </>
        )}
        {type === "text" && isPercentage && (
          <>
            <span className="text-sm" aria-hidden="true">
              %
            </span>
            <span className="text-neutral-500" aria-hidden="true">
              |
            </span>
          </>
        )}
        {type === "number" && isCurrency && (
          <>
            <span className="text-sm" aria-hidden="true">
              $
            </span>
            <span className="text-neutral-500" aria-hidden="true">
              |
            </span>
          </>
        )}
        <input
          onChange={handleChange}
          value={getDisplayValue()}
          type={
            isPassword
              ? showPassword
                ? "text"
                : "password"
              : isPercentage || isCurrency
                ? "text"
                : type
          }
          id={inputId}
          name={name}
          placeholder={placeholder}
          disabled={disabled}
          required={required}
          aria-required={required || undefined}
          aria-invalid={error ? true : undefined}
          aria-describedby={error ? errorId : undefined}
          onKeyDown={(e) => {
            if (isPassword && e.key === " ") {
              e.preventDefault();
            }

            if (onKeyDown) {
              onKeyDown(e);
            }
          }}
          inputMode={isPercentage ? "decimal" : undefined}
          className={`w-full ${classes || ""} focus:outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none ${
            disabled ? "opacity-50 cursor-not-allowed" : ""
          }`}
        />
        {isPassword && (
          <button
            type="button"
            onClick={togglePasswordVisibility}
            className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-600 rounded"
            aria-label={showPassword ? "Hide password" : "Show password"}
          >
            {showPassword ? "Hide" : "Show"}
          </button>
        )}
      </div>
      {error && (
        <p id={errorId} className="text-sm text-red-700" role="alert">
          {error.message}
        </p>
      )}
    </div>
  );
};

export default Input;
